import type { ReactNode } from 'react';
import { Button } from 'react-bootstrap';
import type { ButtonProps } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Actions } from '../Action';

interface ActionButtonProps extends Omit<ButtonProps, 'onClick' | 'disabled' | 'children'> {
  readonly actions: Actions;
  readonly action: string;
  /** Override the action's default label. */
  readonly label?: ReactNode;
  readonly children?: ReactNode;
}

/**
 * Render a single named action from an `Actions` record as a Bootstrap button.
 * Renders nothing when the action is absent (hidden); disabled when present but
 * `enabled` is false. Label and icon fall back to the action's own defaults and
 * can be overridden via `label` / `children`.
 */
export const ActionButton = ({ actions, action, label, children, ...rest }: ActionButtonProps) => {
  const resolved = actions[action];
  if (!resolved) return null;
  return (
    <Button {...rest} disabled={!resolved.enabled} onClick={resolved.execute}>
      {resolved.icon && <FontAwesomeIcon icon={resolved.icon} className="me-2" />}
      {children ?? label ?? resolved.label ?? action}
    </Button>
  );
};
